import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, ExternalLink, Download, BookOpen, Heart, Users, Home, GraduationCap } from "lucide-react";
import SEO from "../components/SEO";

const PARTNERS = [
  {
    name: "Half a Million Kids",
    url: "https://halfamillionkids.org/",
    tagline: "Dedicated to Foster & Trafficked Children",
    desc: "501(c)(3) nonprofit supporting the 500,000+ children in the U.S. foster care system. FC365 tithes forward to HMK.",
    logo: "/assets/partner-hmk-logo.jpg",
  },
  {
    name: "Being Beautiful Foundation",
    url: "https://beingbeautifulfoundation.org/",
    tagline: "Safe, secure and loving homes for abused children",
    desc: "Provides safe homes and healing environments for children who have suffered abuse.",
    logo: "/assets/partner-bbf-logo.png",
    logoBg: "#0a0a0a",
  },
];

const HOTLINES = [
  { name: "Childhelp National Hotline", phone: "1-800-422-4453", desc: "24/7 child abuse reporting and counseling support.", url: "https://www.childhelp.org/hotline/" },
  { name: "National Human Trafficking Hotline", phone: "1-888-373-7888", desc: "Help trafficking victims or report suspected trafficking.", url: "https://humantraffickinghotline.org/" },
  { name: "988 Suicide & Crisis Lifeline", phone: "988", desc: "Free, confidential 24/7 support for emotional distress.", url: "https://988lifeline.org/" },
  { name: "National Runaway Safeline", phone: "1-800-786-2929", desc: "Confidential support for youth in crisis.", url: "https://www.1800runaway.org/" },
  { name: "National Foster Care Helpline", phone: "1-800-394-3366", desc: "Child Welfare Information Gateway resources.", url: "https://www.childwelfare.gov/" },
];

const FOSTER_PARENT_RESOURCES = [
  { name: "Philadelphia DHS", url: "https://www.phila.gov/departments/department-of-human-services/", desc: "Department of Human Services — foster parent pathway for Philadelphia." },
  { name: "PA SWAN Program", url: "https://www.diakon-swan.org/", desc: "Statewide Adoption and Permanency Network — adoption pathway for PA youth." },
  { name: "Turning Points for Children", url: "https://turningpointsforchildren.org/", desc: "Philadelphia-based foster family recruitment, support, and trauma-informed services." },
  { name: "Big Brothers Big Sisters of Philly", url: "https://bbbs.org/", desc: "One-to-one mentorship for youth in the foster care system." },
  { name: "AdoptUSKids", url: "https://www.adoptuskids.org/", desc: "National database and resource for foster care adoption." },
];

const YOUTH_ALUMNI = [
  { name: "FosterClub", url: "https://www.fosterclub.com/", desc: "National network for young people in foster care — community, resources, and policy." },
  { name: "Jim Casey Youth Opportunities Initiative", url: "https://www.aecf.org/work/child-welfare/jim-casey-youth-opportunities-initiative", desc: "Supporting transition-age foster youth with housing, education, and workforce support." },
  { name: "Foster Care to Success", url: "https://www.fc2success.org/", desc: "Scholarships, care packages, and mentorship for youth aging out." },
  { name: "Think of Us", url: "https://www.thinkof-us.org/", desc: "Centering lived experience in child welfare reform and youth advocacy." },
];

const FAITH_BASED = [
  { name: "Christian Alliance for Orphans (CAFO)", url: "https://cafo.org/", desc: "Uniting the Christian community on behalf of orphans and vulnerable children worldwide." },
  { name: "Orphan Care Alliance", url: "https://www.orphancarealliance.org/", desc: "Mobilizing the church to serve children in foster care and adoption." },
  { name: "Show Hope", url: "https://showhope.org/", desc: "Cares for orphans by mobilizing individuals and communities." },
];

const DOWNLOADS = [
  { name: "365 Daily Wall Calendar", url: "/assets/365-challenge-day-1-announcement-copy.jpg" },
  { name: "365 Daily Action Guide", url: "/assets/365-challenge-day-2-announcement-copy.jpg" },
];

function Section({ eyebrow, title, icon: Icon, children }) {
  return (
    <section className="border-b border-fc-border">
      <div className="container-xl py-16">
        <div className="flex items-center gap-3 mb-3">
          {Icon && <Icon size={20} className="text-fc-accent" />}
          <div className="overline text-fc-accent">{eyebrow}</div>
        </div>
        <h2 className="font-display text-3xl md:text-5xl leading-tight mb-10 max-w-3xl">{title}</h2>
        {children}
      </div>
    </section>
  );
}

function LinkCard({ name, url, phone, desc, logo, logoBg, initials, testid }) {
  const Tag = url ? "a" : "div";
  const href = url ? { href: url, target: "_blank", rel: "noreferrer" } : {};
  return (
    <Tag {...href} className="group border border-fc-border bg-fc-bg p-6 card-hover block" data-testid={testid}>
      {logo && (
        <div
          className="h-24 flex items-center justify-center mb-5 border border-fc-border overflow-hidden"
          style={logoBg ? { backgroundColor: logoBg } : { backgroundColor: "#ffffff" }}
        >
          <img src={logo} alt={name} className="max-h-20 max-w-[80%] object-contain" loading="lazy" />
        </div>
      )}
      {!logo && initials && (
        <div className="h-20 flex items-center mb-5">
          <div className="w-16 h-16 border-2 border-fc-ink flex items-center justify-center font-display text-xl text-fc-ink">
            {initials}
          </div>
        </div>
      )}
      {phone && (
        <div className="font-mono text-lg text-fc-accent mb-2 flex items-center gap-2">
          <Phone size={14} /> {phone}
        </div>
      )}
      <div className="font-display text-xl mb-2 group-hover:text-fc-accent transition-colors">{name}</div>
      <p className="text-sm text-fc-muted leading-relaxed">{desc}</p>
      {url && (
        <div className="mt-4 overline text-fc-ink opacity-80 flex items-center gap-1">
          Open Resource <ExternalLink size={12} />
        </div>
      )}
    </Tag>
  );
}

export default function Resources() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="resources-page">
      <SEO
        path="/resources"
        title="Foster Care Resources | Hotlines, Partner Orgs, Downloads & Training"
        description="24/7 crisis hotlines, foster parent pathways (DHS, SWAN, Turning Points), youth alumni networks (FosterClub, Foster Care to Success), faith-based coalitions (CAFO, Show Hope), and free downloadable tools."
      />
      {/* Hero */}
      <section className="border-b border-fc-border">
        <div className="container-xl pt-20 pb-14">
          <div className="overline text-fc-accent mb-5">Resources & Connections</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-5xl">
            Trusted help, <span className="italic text-fc-accent">one click away.</span>
          </h1>
          <p className="mt-6 text-fc-muted text-lg max-w-3xl leading-relaxed">
            Hotlines, partner organizations, fostering pathways, youth support networks, faith-based coalitions, and free downloads. If you need help now — or want to help someone else — start here.
          </p>
        </div>
      </section>

      {/* Emergency / Hotlines */}
      <section className="bg-fc-ink text-fc-bg">
        <div className="container-xl py-14">
          <div className="flex items-center gap-3 mb-3">
            <Phone size={20} className="text-fc-accent" />
            <div className="overline opacity-70">Crisis Hotlines · 24/7</div>
          </div>
          <h2 className="font-display text-3xl md:text-5xl mb-10 max-w-3xl leading-tight">If it's urgent, <em className="text-fc-accent">call now.</em></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-fc-bg/20">
            {HOTLINES.map((h, i) => (
              <a key={i} href={h.url} target="_blank" rel="noopener noreferrer" className="p-6 border-r border-b border-fc-bg/20 hover:bg-fc-bg/5 transition-colors block" data-testid={`hotline-${i}`}>
                <div className="font-mono text-2xl md:text-3xl text-fc-accent mb-2">{h.phone}</div>
                <div className="font-display text-lg mb-2">{h.name}</div>
                <p className="text-sm opacity-70 leading-relaxed">{h.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Partner orgs */}
      <Section eyebrow="Trusted Partners · 501(c)(3)" title="Organizations we stand with." icon={Heart}>
        <div className="grid md:grid-cols-2 gap-6">
          {PARTNERS.map((p, i) => (
            <LinkCard key={p.name} {...p} testid={`partner-${i}`} />
          ))}
        </div>
      </Section>

      {/* Become a foster parent */}
      <Section eyebrow="For Prospective Foster Parents" title="Where to start the journey." icon={Home}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FOSTER_PARENT_RESOURCES.map((r, i) => (
            <LinkCard key={r.name} {...r} testid={`foster-resource-${i}`} />
          ))}
        </div>
      </Section>

      {/* Youth + Alumni */}
      <Section eyebrow="For Foster Youth & Alumni" title="Built by and for those with lived experience." icon={Users}>
        <div className="grid md:grid-cols-2 gap-5">
          {YOUTH_ALUMNI.map((r, i) => (
            <LinkCard key={r.name} {...r} testid={`youth-resource-${i}`} />
          ))}
        </div>
      </Section>

      {/* Faith-based */}
      <Section eyebrow="Faith-Based Coalitions" title="Mobilizing the church for the fatherless." icon={BookOpen}>
        <div className="grid md:grid-cols-3 gap-5">
          {FAITH_BASED.map((r, i) => (
            <LinkCard key={r.name} {...r} testid={`faith-resource-${i}`} />
          ))}
        </div>
      </Section>

      {/* Downloads */}
      <section className="bg-fc-surface border-b border-fc-border">
        <div className="container-xl py-16">
          <div className="flex items-center gap-3 mb-3">
            <Download size={20} className="text-fc-accent" />
            <div className="overline text-fc-accent">Free Downloads</div>
          </div>
          <h2 className="font-display text-3xl md:text-5xl mb-10 max-w-3xl leading-tight">Tools you can print, share, or post.</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {DOWNLOADS.map((d, i) => (
              <a key={d.name} href={d.url} target="_blank" rel="noopener noreferrer" download className="border border-fc-border bg-fc-bg p-6 card-hover flex items-center justify-between gap-4" data-testid={`download-${i}`}>
                <div>
                  <div className="overline text-fc-muted mb-2">PDF · PNG</div>
                  <div className="font-display text-xl">{d.name}</div>
                </div>
                <Download size={24} className="text-fc-accent shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Quiz / self-assessment */}
      <section className="container-xl py-20 max-w-3xl text-center">
        <div className="flex items-center gap-3 mb-3 justify-center">
          <GraduationCap size={20} className="text-fc-accent" />
          <div className="overline text-fc-accent">Self-Assessment</div>
        </div>
        <h2 className="font-display text-3xl md:text-5xl mb-6 leading-tight">
          "Are You Ready to Change a Life — <em className="text-fc-accent">365 Days a Year?</em>"
        </h2>
        <p className="text-fc-muted leading-relaxed mb-8">
          This 2-minute quiz helps you assess your emotional, practical, and lifestyle readiness to foster a child. Your answers are confidential and meant to guide — not judge.
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/am-i-ready-to-foster" className="btn-primary" data-testid="ready-quiz-link">Start the Quiz</Link>
          <Link to="/get-involved" className="btn-outline" data-testid="resources-give">Ways to Give</Link>
        </div>
      </section>

      {/* Help us grow list */}
      <section className="bg-fc-ink text-fc-bg">
        <div className="container-xl py-16 text-center max-w-3xl">
          <div className="overline opacity-70 mb-4">Know a resource we should add?</div>
          <h2 className="font-display text-3xl md:text-4xl mb-6 leading-tight">
            This list grows with <em className="text-fc-accent">the community.</em>
          </h2>
          <p className="opacity-75 leading-relaxed mb-8">
            If your organization serves foster youth, families, or alumni — or if there's a trusted national resource we should feature — let us know and we'll review it.
          </p>
          <Link to="/contact" className="btn-primary" style={{background:"#F9F9F6", color:"#121212", borderColor:"#F9F9F6"}} data-testid="resources-suggest">Suggest a Resource</Link>
        </div>
      </section>
    </div>
  );
}
